import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';

export interface TextChartContent {
  content: string | null;
  isLoading: boolean;
  error: string | null;
}

export type TextChartContentMap = Map<string, TextChartContent>;

export interface UpdateChartContentParams {
  chartId: string;
  content: string | null;
  isLoading: boolean;
  error: string | null;
}

export type AiSummaryState = {
  summary: string;
  loading: boolean;
  error: string | null;
};

interface TextChartContentContextValue {
  textCharts: TextChartContentMap;
  updateChartContent: (params: UpdateChartContentParams) => void;
  areAllLoaded: boolean;
  getAllTextCharts: () => { chartId: string; content: TextChartContent }[];
  aiSummary: AiSummaryState;
  setAiSummary: Dispatch<SetStateAction<AiSummaryState>>;
}

const TextChartContentContext = createContext<
  TextChartContentContextValue | undefined
>(undefined);

const initialAiSummary: AiSummaryState = {
  summary: '',
  loading: true,
  error: null,
};

export const TextChartContentProvider = ({ children }: PropsWithChildren) => {
  const [textCharts, setTextCharts] = useState<TextChartContentMap>(new Map());
  const [aiSummary, setAiSummary] = useState<AiSummaryState>(initialAiSummary);

  const updateChartContent = useCallback(
    ({ chartId, content, isLoading, error }: UpdateChartContentParams) => {
      setTextCharts((prev) => {
        const next = new Map(prev);
        if (content === null && !isLoading && error === null) {
          next.delete(chartId);
        } else {
          next.set(chartId, { content, isLoading, error });
        }
        return next;
      });
    },
    []
  );

  const areAllLoaded = useCallback(() => {
    const mapSize = textCharts.size;
    if (mapSize === 0) {
      return false;
    }
    for (const chart of textCharts.values()) {
      if (chart.isLoading) {
        return false;
      }
    }
    return true;
  }, [textCharts]);

  const getAllTextCharts = useCallback(
    () =>
      Array.from(textCharts.entries()).map(([chartId, content]) => ({
        chartId,
        content,
      })),
    [textCharts]
  );

  const value: TextChartContentContextValue = {
    textCharts,
    updateChartContent,
    areAllLoaded: areAllLoaded(),
    getAllTextCharts,
    aiSummary,
    setAiSummary,
  };

  return (
    <TextChartContentContext.Provider value={value}>
      {children}
    </TextChartContentContext.Provider>
  );
};

export const useTextChartContent = () => {
  const context = useContext(TextChartContentContext);
  if (context === undefined) {
    throw new Error(
      'useTextChartContent must be used within a TextChartContentProvider'
    );
  }
  return context;
};
